// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars } from '../components/charts';
import { byCompany, type Group } from '../analysis';
import { OVER, UNDER, positionColour } from '../sectors';
import { escapeHtml, formatCompact, formatNumber } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(2)} Mt`;

export function renderCompanies(root: HTMLElement, ctx: ViewContext): void {
  const { facilities } = ctx.data;
  const companies = byCompany(facilities);
  const top = companies.slice(0, 20);
  let selected = companies[0].key;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The companies behind the emissions</h1>
      <p class="view-sub">
        Facilities rolled up to their ${glossaryTerm('responsible', 'responsible emitter')} — the parent company legally
        accountable for them. One company can run many facilities, so the corporate picture concentrates emissions further
        than any single site. Bar colour shows whether the company's facilities were net over (ember) or under (teal)
        baseline overall. Click to drill in.
      </p>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">Top 20 companies by covered emissions</div>
        <div class="panel-sub">Aggregate covered emissions across all a company's Safeguard facilities.</div>
        <div class="chart-scroll" data-role="bars"></div>
      </div>
      <div class="panel" data-role="detail"></div>
    </div>
  `;

  const barsEl = root.querySelector('[data-role="bars"]') as HTMLElement;
  const detailEl = root.querySelector('[data-role="detail"]') as HTMLElement;

  const drawBars = () => {
    barsEl.innerHTML = '';
    barsEl.appendChild(
      horizontalBars(
        top.map((c) => ({
          id: c.key,
          label: c.label.length > 26 ? c.label.slice(0, 25) + '…' : c.label,
          value: c.covered,
          colour: c.excess > 0 ? OVER : UNDER,
          tip: `${c.label}\n${mt(c.covered)} across ${c.count} facilities\nnet ${c.excess > 0 ? '+' : ''}${formatCompact(c.excess)} vs baseline · ${c.nOver} over`,
        })),
        { width: 640, labelWidth: 200, valueFormat: (v) => formatCompact(v), onClick: (id) => select(id) },
      ),
    );
  };

  const drawDetail = () => {
    const c = companies.find((x) => x.key === selected) as Group;
    detailEl.innerHTML = `
      <div class="panel-title">${escapeHtml(c.label)}</div>
      <div class="panel-sub">${mt(c.covered)} across ${c.count} ${c.count === 1 ? 'facility' : 'facilities'} · ${c.nOver} over baseline · net ${c.excess > 0 ? '+' : ''}${formatCompact(c.excess)} vs baseline · earned ${formatCompact(c.smcsIssued)} credits, surrendered ${formatCompact(c.unitsSurrendered)}</div>
      <div class="table-scroll" style="max-height:460px;overflow-y:auto">
        <table class="data">
          <thead><tr><th>Facility</th><th class="t-num">Covered</th><th class="t-num">vs base</th></tr></thead>
          <tbody>
            ${c.facilities
              .map(
                (f) => `<tr data-slug="${escapeHtml(f.slug)}">
                  <td class="loc-cell">${escapeHtml(f.name)}<br><span class="muted" style="font-size:var(--font-size-xs)">${escapeHtml(f.sector)}${f.state !== 'MULTI' ? ` · ${f.state}` : ''}</span></td>
                  <td class="t-num">${formatCompact(f.covered)}</td>
                  <td class="t-num" style="color:${positionColour(f.excess)}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>`;
  };

  const select = (key: string) => {
    if (!companies.some((c) => c.key === key)) return;
    selected = key;
    drawDetail();
  };

  detailEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-slug]');
    if (tr) ctx.openFacility(tr.getAttribute('data-slug') as string);
  });

  drawBars();
  drawDetail();

  // Full company table below.
  const panel = document.createElement('div');
  panel.className = 'panel';
  panel.style.marginTop = 'var(--space-lg)';
  panel.innerHTML = `
    <div class="panel-title">Every responsible company</div>
    <div class="panel-sub">${formatNumber(companies.length)} companies. Click a row to drill into its facilities above.</div>
    <div class="table-scroll" style="max-height:520px;overflow-y:auto">
      <table class="data">
        <thead><tr><th>Company</th><th class="t-num">Covered</th><th class="t-num">Facilities</th><th class="t-num">Over</th><th class="t-num">Net vs base</th></tr></thead>
        <tbody>
          ${companies
            .map(
              (c) => `<tr data-key="${escapeHtml(c.key)}">
                <td>${escapeHtml(c.label)}</td>
                <td class="t-num">${formatCompact(c.covered)}</td>
                <td class="t-num">${formatNumber(c.count)}</td>
                <td class="t-num">${formatNumber(c.nOver)}</td>
                <td class="t-num" style="color:${positionColour(c.excess)}">${c.excess > 0 ? '+' : ''}${formatCompact(c.excess)}</td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>`;
  panel.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-key]');
    if (tr) {
      select(tr.getAttribute('data-key') as string);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
  root.appendChild(panel);
}
